import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import { useSubscription } from '@/context/SubscriptionContext';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Search, Menu, User, LogOut, Settings, Bookmark, Crown, Bot, Users, Eye, Bell } from 'lucide-react';
import { useState, useEffect } from 'react';

const LOGO_URL = 'https://static.prod-images.emergentagent.com/jobs/17b3d771-b1f5-4098-a241-9e4a23cb7105/images/d723499229a122d3a5167c0be3fe90a0120654740a7a8849a0415df0e44449c8.png';

export const Navbar = ({ onMenuClick }) => {
  const { user, logout } = useAuth();
  const { isPremium } = useSubscription();
  const navigate = useNavigate();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [bookmarkCount, setBookmarkCount] = useState(0);

  useEffect(() => {
    const updateCount = () => {
      const bookmarks = JSON.parse(localStorage.getItem('watchtower_bookmarks') || '[]');
      setBookmarkCount(bookmarks.length);
    };
    updateCount();
    window.addEventListener('storage', updateCount);
    return () => window.removeEventListener('storage', updateCount);
  }, []);

  const handleSearch = (e) => {
    e.preventDefault();
    if (searchQuery.trim()) {
      navigate(`/search?q=${encodeURIComponent(searchQuery.trim())}`);
      setSearchQuery('');
    }
  };

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  return (
    <header className="sticky top-0 z-40 masthead masthead-glass" data-testid="navbar">
      {/* Primary Nav Row - Logo, Core Links, Search, User */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-14">
          {/* Mobile Menu Button */}
          <button
            onClick={onMenuClick}
            className="lg:hidden w-10 h-10 flex items-center justify-center rounded-xl bg-gradient-to-r from-blue-500 to-purple-600 text-white shadow-lg hover:shadow-xl transition-shadow mr-3"
            data-testid="mobile-menu-button"
          >
            <Menu className="h-5 w-5" />
          </button>
          
          {/* Logo */}
          <Link to="/" className="flex items-center gap-2 flex-shrink-0" data-testid="navbar-logo">
            <img src={LOGO_URL} alt="The Watchtower" className="h-8 w-8 object-contain" />
            <div className="font-serif font-medium tracking-tight leading-tight hidden sm:block">
              <span className="text-sm">The</span>
              <span className="text-lg sm:text-xl ml-1">Watchtower</span>
            </div>
          </Link>

          {/* Primary Desktop Navigation - Core Pages */}
          <nav className="hidden lg:flex items-center space-x-5 ml-8">
            <Link to="/" className="text-sm font-medium tracking-wide uppercase link-underline" data-testid="nav-home">
              Home
            </Link>
            <Link to="/compare" className="text-sm font-medium tracking-wide uppercase link-underline" data-testid="nav-compare">
              Compare
            </Link>
            <Link to="/elections" className="text-sm font-medium tracking-wide uppercase link-underline" data-testid="nav-elections">
              Elections
            </Link>
            <Link to="/polls" className="text-sm font-medium tracking-wide uppercase link-underline text-purple-600" data-testid="nav-polls">
              Polls
            </Link>
            <div className="relative group">
              <Link to="/odds" className="text-sm font-medium tracking-wide uppercase link-underline text-amber-600 flex items-center gap-1" data-testid="nav-odds">
                Odds
                <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                </svg>
              </Link>
              <div className="absolute top-full left-0 mt-1 bg-white rounded-lg shadow-lg border border-slate-200 py-2 min-w-[180px] opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all z-50">
                <Link to="/odds" className="block px-4 py-2 text-sm hover:bg-slate-50 text-slate-700">
                  MP Seat Odds
                </Link>
                <Link to="/council-odds" className="block px-4 py-2 text-sm hover:bg-slate-50 text-slate-700">
                  Council Odds
                </Link>
                <div className="border-t border-slate-100 my-1"></div>
                <Link to="/election-history" className="block px-4 py-2 text-sm hover:bg-slate-50 text-slate-700">
                  Election History (1955-2024)
                </Link>
                <Link to="/mp-expenses" className="block px-4 py-2 text-sm hover:bg-slate-50 text-slate-700 text-emerald-600 font-medium">
                  MP Expenses Leaderboard
                </Link>
              </div>
            </div>
            <Link to="/map" className="text-sm font-medium tracking-wide uppercase link-underline text-blue-600" data-testid="nav-map">
              Map
            </Link>
            <Link to="/submit-tip" className="text-sm font-medium tracking-wide uppercase link-underline" data-testid="nav-submit-tip">
              Submit Tip
            </Link>
            <Link to="/donate" className="text-sm font-medium tracking-wide uppercase link-underline text-red-600" data-testid="nav-donate">
              Donate
            </Link>
            <Link to="/shop" className="text-sm font-medium tracking-wide uppercase link-underline" data-testid="nav-shop">
              Shop
            </Link>
          </nav>

          {/* Search and Auth */}
          <div className="flex items-center space-x-2 sm:space-x-3">
            {/* Alerts Bell Icon */}
            <Link 
              to="/settings#notifications" 
              className="hidden sm:flex items-center p-2 hover:bg-slate-100 rounded-lg transition-colors group"
              data-testid="nav-alerts"
              title="Get Alerts"
            >
              <Bell className="h-5 w-5 text-slate-600 group-hover:text-amber-500 transition-colors" />
            </Link>
            
            {/* Reading List Icon */}
            <Link 
              to="/reading-list" 
              className="hidden sm:flex items-center relative p-2 hover:bg-slate-100 rounded-lg transition-colors"
              data-testid="nav-reading-list"
            >
              <Bookmark className="h-5 w-5 text-slate-600" />
              {bookmarkCount > 0 && (
                <span className="absolute -top-0.5 -right-0.5 bg-blue-600 text-white text-[10px] font-bold rounded-full h-4 min-w-[16px] flex items-center justify-center px-1">
                  {bookmarkCount > 9 ? '9+' : bookmarkCount}
                </span>
              )}
            </Link>
            
            {/* Search */}
            <form onSubmit={handleSearch} className="hidden sm:flex items-center">
              <input
                type="text"
                placeholder="Search..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="search-bar w-32 lg:w-48 text-sm"
                data-testid="search-input"
              />
              <button type="submit" className="ml-2 p-2 hover:bg-slate-100" data-testid="search-submit">
                <Search className="h-4 w-4" />
              </button>
            </form>

            {/* Auth Buttons */}
            {user ? (
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="ghost" className="flex items-center space-x-2" data-testid="user-menu-trigger">
                    <User className="h-4 w-4" />
                    <span className="hidden lg:inline text-sm">{user.username}</span>
                    {user.is_admin && <span className="text-xs bg-red-100 text-red-600 px-1 rounded">Admin</span>}
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-48">
                  <DropdownMenuItem onClick={() => navigate('/subscribe')} data-testid="subscribe-link">
                    <Settings className="h-4 w-4 mr-2" />
                    Subscription
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => navigate('/settings')} data-testid="settings-link">
                    <Settings className="h-4 w-4 mr-2" />
                    Alert Settings
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => navigate('/watchlist')} data-testid="watchlist-link">
                    <Eye className="h-4 w-4 mr-2" />
                    My Watchlist
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => navigate('/referral')} data-testid="referral-link">
                    <User className="h-4 w-4 mr-2" />
                    Refer Friends
                  </DropdownMenuItem>
                  <DropdownMenuSeparator />
                  {user.is_admin && (
                    <>
                      <DropdownMenuItem onClick={() => navigate('/admin')} data-testid="admin-link">
                        <Settings className="h-4 w-4 mr-2" />
                        Admin Panel
                      </DropdownMenuItem>
                      <DropdownMenuSeparator />
                    </>
                  )}
                  <DropdownMenuItem onClick={handleLogout} data-testid="logout-button">
                    <LogOut className="h-4 w-4 mr-2" />
                    Logout
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            ) : (
              <div className="hidden sm:flex items-center space-x-2">
                <Link to="/login">
                  <Button variant="ghost" className="text-sm" data-testid="login-button">
                    Login
                  </Button>
                </Link>
                <Link to="/register">
                  <Button className="btn-primary text-xs" data-testid="register-button">
                    Register
                  </Button>
                </Link>
              </div>
            )}

            {/* Mobile Menu */}
            <button 
              className="lg:hidden p-2"
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              data-testid="mobile-menu-toggle"
            >
              <Menu className="h-5 w-5" />
            </button>
          </div>
        </div>
      </div>

      {/* Secondary Nav Row - Feature Links (Desktop Only) */}
      <div className="hidden lg:block border-t border-slate-200/50 bg-slate-50/50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <nav className="flex items-center justify-center space-x-6 h-10">
            <Link 
              to="/politicians?type=mp"
              className="flex items-center gap-1.5 text-xs font-semibold tracking-wide uppercase text-purple-600 hover:text-purple-700 transition-colors"
              data-testid="nav-mps"
            >
              <Users className="h-3.5 w-3.5" />
              MPs
            </Link>
            <Link 
              to="/politicians?type=councillor"
              className="flex items-center gap-1.5 text-xs font-semibold tracking-wide uppercase text-indigo-600 hover:text-indigo-700 transition-colors"
              data-testid="nav-councillors"
            >
              <Users className="h-3.5 w-3.5" />
              Councillors
            </Link>
            <Link 
              to="/ai-research"
              className="flex items-center gap-1.5 text-xs font-semibold tracking-wide uppercase text-cyan-600 hover:text-cyan-700 transition-colors"
              data-testid="nav-ai-research"
            >
              <Bot className="h-3.5 w-3.5" />
              AI Search
            </Link>
            {!isPremium && (
              <Link 
                to="/subscribe" 
                className="flex items-center gap-1.5 text-xs font-bold tracking-wide uppercase bg-gradient-to-r from-amber-500 to-amber-600 text-white px-3 py-1 rounded-full hover:from-amber-600 hover:to-amber-700 transition-all"
                data-testid="nav-subscribe"
              >
                <Crown className="h-3.5 w-3.5" />
                Subscribe
              </Link>
            )}
            {user?.is_admin && (
              <Link 
                to="/admin" 
                className="text-xs font-semibold tracking-wide uppercase text-orange-600 hover:text-orange-700 transition-colors"
                data-testid="nav-admin"
              >
                Admin
              </Link>
            )}
          </nav>
        </div>
      </div>

      {/* Mobile Menu Dropdown */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {mobileMenuOpen && (
          <div className="lg:hidden py-4 border-t border-slate-200 animate-fadeIn" data-testid="mobile-menu">
            <nav className="flex flex-col space-y-4">
              <Link 
                to="/" 
                onClick={() => setMobileMenuOpen(false)}
                className="text-sm font-medium tracking-wide uppercase"
              >
                News
              </Link>
              <Link 
                to="/compare" 
                onClick={() => setMobileMenuOpen(false)}
                className="text-sm font-medium tracking-wide uppercase"
              >
                Compare Parties
              </Link>
              <Link 
                to="/elections" 
                onClick={() => setMobileMenuOpen(false)}
                className="text-sm font-medium tracking-wide uppercase"
              >
                Elections
              </Link>
              <Link 
                to="/reading-list" 
                onClick={() => setMobileMenuOpen(false)}
                className="text-sm font-medium tracking-wide uppercase flex items-center gap-2"
              >
                <Bookmark className="h-4 w-4" />
                Reading List
                {bookmarkCount > 0 && (
                  <span className="bg-blue-600 text-white text-xs px-1.5 rounded-full">
                    {bookmarkCount}
                  </span>
                )}
              </Link>
              <Link 
                to="/submit-tip" 
                onClick={() => setMobileMenuOpen(false)}
                className="text-sm font-medium tracking-wide uppercase"
              >
                Submit Tip
              </Link>
              <Link 
                to="/donate" 
                onClick={() => setMobileMenuOpen(false)}
                className="text-sm font-medium tracking-wide uppercase text-red-600"
              >
                Donate
              </Link>
              <Link 
                to="/shop" 
                onClick={() => setMobileMenuOpen(false)}
                className="text-sm font-medium tracking-wide uppercase"
              >
                Shop MOCGA
              </Link>
              <Link 
                to="/politicians"
                onClick={() => setMobileMenuOpen(false)}
                className="text-sm font-medium tracking-wide uppercase text-purple-600 flex items-center gap-2"
              >
                <Users className="h-4 w-4" />
                Politicians
              </Link>
              <Link 
                to="/ai-research"
                onClick={() => setMobileMenuOpen(false)}
                className="text-sm font-medium tracking-wide uppercase text-cyan-600 flex items-center gap-2"
              >
                <Bot className="h-4 w-4" />
                AI Research
              </Link>
              {user?.is_admin && (
                <Link 
                  to="/admin" 
                  onClick={() => setMobileMenuOpen(false)}
                  className="text-sm font-medium tracking-wide uppercase text-orange-600"
                >
                  Admin Panel
                </Link>
              )}
              <form onSubmit={handleSearch} className="flex items-center">
                <input
                  type="text"
                  placeholder="Search..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="search-bar flex-1 text-sm"
                />
                <button type="submit" className="ml-2 p-2">
                  <Search className="h-4 w-4" />
                </button>
              </form>
              {!user && (
                <div className="flex space-x-2 pt-2">
                  <Link to="/login" onClick={() => setMobileMenuOpen(false)} className="flex-1">
                    <Button variant="outline" className="w-full text-sm">Login</Button>
                  </Link>
                  <Link to="/register" onClick={() => setMobileMenuOpen(false)} className="flex-1">
                    <Button className="w-full btn-primary text-xs">Register</Button>
                  </Link>
                </div>
              )}
            </nav>
          </div>
        )}
      </div>
    </header>
  );
};

export default Navbar;
